import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

import SEO from '../../components/feature/SEO';
import { EXCHANGE_RATES } from '@/config/exchangeRates';
import { PLANS_BY_ID, YEARLY_DISCOUNT } from '@/config/plans';
import { isUpgradePopupEnabled } from '../../utils/upgradePopupMode';

// Map plan IDs to the numbered plan identifiers used downstream (0-4)
const PLAN_NUMBER_BY_ID: Record<string, string> = {
  free: '0',
  starter: '1',
  core: '2',
  business: '3',
  proAdvisor: '4',
};

// Map pricing page plan IDs to the numeric plan IDs the payment page expects
const PAYMENT_PLAN_ID_BY_PLAN: Record<string, string> = {
  starter: '2',
  core: '3',
  business: '4',
  proAdvisor: '5',
};

// Write cookies so an ASP (Classic ASP / ASP.NET) site can read the selected plan
function writePlanCookie(planId: string) {
  const planNumber = PLAN_NUMBER_BY_ID[planId] ?? planId;
  const domainName = window.location.hostname;
  document.cookie = `planNumber=${encodeURIComponent(planNumber)}; path=/; max-age=86400; SameSite=Lax`;
  document.cookie = `domainName=${encodeURIComponent(domainName)}; path=/; max-age=86400; SameSite=Lax`;
}

export default function Pricing() {

  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [currency, setCurrency] = useState<'USD' | 'EUR' | 'GBP' | 'AUD'>('USD');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
	const navigate = useNavigate();

  const currencyContainerRef = useRef<HTMLDivElement>(null);
  const currencyBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [pillStyle, setPillStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  const currencies: Array<'EUR' | 'USD' | 'GBP' | 'AUD'> = ['EUR', 'USD', 'GBP', 'AUD'];

  const updatePillPosition = useCallback(() => {
    const container = currencyContainerRef.current;
    const activeBtn = currencyBtnRefs.current[currency];
    if (container && activeBtn) {
      const containerRect = container.getBoundingClientRect();
      const btnRect = activeBtn.getBoundingClientRect();
      setPillStyle({
        left: btnRect.left - containerRect.left,
        width: btnRect.width,
      });
    }
  }, [currency]);

  useEffect(() => {
    updatePillPosition();
    window.addEventListener('resize', updatePillPosition);
    return () => window.removeEventListener('resize', updatePillPosition);
  }, [updatePillPosition]);

  // Base prices in USD
  const basePrices = {
    free: 0,
    starter: PLANS_BY_ID.starter.monthlyUsd,
    core: PLANS_BY_ID.core.monthlyUsd,
    business: PLANS_BY_ID.business.monthlyUsd,
    proAdvisor: PLANS_BY_ID.proAdvisor.monthlyUsd
  };

  // Calculate prices based on billing cycle (always in USD base)
  const calculatePrice = (monthlyPrice: number) => {
    if (billingCycle === 'yearly') {
      return monthlyPrice * 12 * YEARLY_DISCOUNT; // 15% discount
    }
    return monthlyPrice;
  };

  // Convert to selected currency for display
  const convertCurrency = (usdPrice: number) => {
    if (currency === 'USD') return usdPrice;
    return usdPrice * EXCHANGE_RATES[currency];
  };

  // Get currency symbol
  const getCurrencySymbol = () => {
    switch (currency) {
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'AUD': return 'A$';
      default: return '$';
    }
  };

  // Format price display
  const formatPrice = (monthlyUsdPrice: number) => {
    const calculatedUsdPrice = calculatePrice(monthlyUsdPrice);
    const displayPrice = convertCurrency(calculatedUsdPrice);
    const symbol = getCurrencySymbol();
    
    // For USD, show whole numbers for yearly, regular for monthly
    if (currency === 'USD') {
      if (billingCycle === 'yearly') {
        return `${symbol}${Math.round(displayPrice)}`;
      }
      return `${symbol}${displayPrice}`;
    }
    
    // For other currencies, always show 2 decimal places
    return `${symbol}${displayPrice.toFixed(2)}`;
  };

  // Get period text
  const getPeriodText = () => {
    return billingCycle === 'yearly' ? '/year' : '/month';
  };

  const handlePlanClick = (planId: string) => {
	writePlanCookie(planId);
    if (isUpgradePopupEnabled()) {
      setShowUpgradeModal(true);
    } else {
      const term = billingCycle === 'yearly' ? 'Y' : 'M';
      if (planId === 'free') {
        navigate('/payment-free');
      } else {
        navigate('/payment', { state: { planId: PAYMENT_PLAN_ID_BY_PLAN[planId] ?? planId, term } });
      }
    }
  };
	
	  // Temporary test hook: run the normal payment flow for plan 9 ($1/month)
  const handleTestPlanClick = () => {
    navigate('/payment', { state: { planId: '9', term: 'M' } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <SEO
        title="QBOVerifier Pricing – Free & Paid Invoice Fraud Protection Plans"
        description="Choose the QBOVerifier plan that fits your business. From a free plan with 5 invoices/month to ProAdvisor for bookkeepers. Transparent pricing, no hidden fees. Protect QuickBooks invoices today."
        keywords="QBOVerifier pricing, QuickBooks invoice verification cost, invoice fraud protection plans, free invoice security, QuickBooks extension pricing"
        canonical="/pricing"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Is there a free trial available?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! You can select the Free plan to experience the full value of the solution. The first 5 invoices are styled for your company. Subsequent invoices default to the standard style.',
                },
              },
              {
                '@type': 'Question',
                name: 'What happens if I exceed my invoice limit?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'We notify you when approaching your limit. You can upgrade your plan anytime or purchase additional invoice credits.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can I cancel my subscription anytime?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. You can cancel at any time. Your account remains active until the end of your billing period.',
                },
              },
              {
                '@type': 'Question',
                name: 'Do you offer discounts for annual billing?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! Save 15% when you choose annual billing. Contact our sales team for custom Enterprise pricing.',
                },
              },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: 'QBOVerifier',
            description: 'QuickBooks invoice fraud protection extension with cryptographic verification stamps.',
            offers: [
              {
                '@type': 'Offer',
                name: 'Free Plan',
                price: '0',
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
              },
              {
                '@type': 'Offer',
                name: 'Starter Plan',
                price: String(PLANS_BY_ID.starter.monthlyUsd),
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
              },
              {
                '@type': 'Offer',
                name: 'Core Plan',
                price: String(PLANS_BY_ID.core.monthlyUsd),
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
              },
              {
                '@type': 'Offer',
                name: 'Business Plan',
                price: String(PLANS_BY_ID.business.monthlyUsd),
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
              },
              {
                '@type': 'Offer',
                name: 'ProAdvisor Plan',
                price: String(PLANS_BY_ID.proAdvisor.monthlyUsd),
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
              },
            ],
          },
        ]}
      />
      <Header />

      {/* Upgrade In Progress Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 relative animate-fade-in text-center">
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl w-6 h-6 flex items-center justify-center"></i>
            </button>
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-5">
              <i className="ri-tools-fill text-white text-2xl w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Upgrade In Progress</h3>
            <p className="text-gray-500 mb-6">We Will Return Shortly</p>
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the plan that fits your business needs. All plans include our core security features and the free professional invoice and estimates template, with no hidden fees.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Billing Toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex items-center bg-white rounded-full p-1 shadow-lg border-2 border-gray-200">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap ${
                  billingCycle === 'monthly'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap ${
                  billingCycle === 'yearly'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                  Save 15%
                </span>
              </button>
            </div>
          </div>

          {/* Currency Selector */}
          <div className="text-center mb-8">
            <div className="inline-flex flex-col items-center">
              <div
                ref={currencyContainerRef}
                className="relative inline-flex items-center bg-white rounded-full p-1 shadow-md border border-gray-200"
              >
                <div 
                  className="absolute top-1 bottom-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-300 ease-in-out"
                  style={{
                    width: `${pillStyle.width}px`,
                    left: `${pillStyle.left}px`,
                  }}
                ></div>
                {currencies.map((cur) => (
                  <button
                    key={cur}
                    ref={(el) => { currencyBtnRefs.current[cur] = el; }}
                    onClick={() => setCurrency(cur)}
                    className={`relative z-10 px-7 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      currency === cur ? 'text-white' : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {cur === 'EUR' ? 'Euros' : cur}
                  </button>
                ))}
              </div>
              <p className={`mt-3 text-sm font-medium transition-all duration-300 ${
                currency !== 'USD' ? 'text-gray-600' : 'text-transparent select-none'
              }`}>
                {currency !== 'USD' ? 'Approximate Conversion. Billing is in USD.' : '\u00A0'}
              </p>
            </div>
          </div>

          {/* First Row - 3 Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8 max-w-6xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border-2 border-gray-100 hover:border-blue-200 flex flex-col h-full">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
                <div className="text-4xl font-bold text-gray-900 mb-1">
                  {getCurrencySymbol()}0<span className="text-lg text-gray-600">/month</span>
                </div>
              </div>
              
              <ul className="space-y-2.5 mb-8 flex-grow">
                <li className="flex items-center gap-3 invisible">
                  <span className="text-gray-700 text-sm">&nbsp;</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">QBOVerifier bioStamp (anti-tamper)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Styled documents</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">5 documents/month</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-close-line text-gray-400 text-xl flex-shrink-0"></i>
                  <span className="text-gray-400 text-sm">Custom branding (logo + colors + footer)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud checks (tamper + integrity validation)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-close-line text-gray-400 text-xl flex-shrink-0"></i>
                  <span className="text-gray-400 text-sm">Fraud alerts (email notifications)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-close-line text-gray-400 text-xl flex-shrink-0"></i>
                  <span className="text-gray-400 text-sm">Fraud reporting (history / audit trail)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Email support (community)</span>
                </li>
              </ul>
              
              <button
                onClick={() => handlePlanClick('free')}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap text-center block cursor-pointer mt-auto"
              >
                Get Started Free
              </button>
            </div>

            {/* Starter Plan */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border-2 border-blue-200 hover:border-blue-300 flex flex-col h-full relative">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Starter</h3>
                <div className="text-4xl font-bold text-gray-900 mb-1">
                  {formatPrice(basePrices.starter)}<span className="text-lg text-gray-600">{getPeriodText()}</span>
                </div>
              </div>
              
              <ul className="space-y-2.5 mb-8 flex-grow">
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Everything in Free</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">QBOVerifier bioStamp (anti-tamper)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Styled documents</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">100 documents/month</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Custom branding (banner)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud checks (tamper + integrity validation)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-close-line text-gray-400 text-xl flex-shrink-0"></i>
                  <span className="text-gray-400 text-sm">Fraud alerts (email notifications)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-close-line text-gray-400 text-xl flex-shrink-0"></i>
                  <span className="text-gray-400 text-sm">Fraud reporting (history / audit trail)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Email support</span>
                </li>
              </ul>
              
              <button
                onClick={() => handlePlanClick('starter')}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap text-center block cursor-pointer mt-auto"
              >
                Select
              </button>
            </div>

            {/* Core Plan */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border-2 border-blue-200 hover:border-blue-300 flex flex-col h-full relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Better Value
                </span>
              </div>
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Core</h3>
                <div className="text-4xl font-bold text-gray-900 mb-1">
                  {formatPrice(basePrices.core)}<span className="text-lg text-gray-600">{getPeriodText()}</span>
                </div>
              </div>
              
              <ul className="space-y-2.5 mb-8 flex-grow">
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Everything in Starter</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">QBOVerifier bioStamp (anti-tamper)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Styled documents</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">600 documents/month</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Custom branding (banner + logo + colors + footer)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud checks (tamper + integrity validation)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud alerts (email notifications)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud reporting (advanced / export)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Priority email support</span>
                </li>
              </ul>
              
              <button
                onClick={() => handlePlanClick('core')}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap text-center block cursor-pointer mt-auto"
              >
                Select
              </button>
            </div>
          </div>

          {/* Second Row - 3 Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Business Plan */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border-2 border-indigo-200 hover:border-indigo-300 flex flex-col h-full relative">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Business</h3>
                <p className="text-sm text-transparent mb-2">&nbsp;</p>
                <div className="text-4xl font-bold text-gray-900 mb-1">
                  {formatPrice(basePrices.business)}<span className="text-lg text-gray-600">{getPeriodText()}</span>
                </div>
              </div>
              
              <ul className="space-y-2.5 mb-8 flex-grow">
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Everything in Core</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">QBOVerifier bioStamp (anti-tamper)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Styled documents</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">1,000 documents/month</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Custom branding (banner + logo + colors + footer)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud checks (tamper + integrity validation)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud alerts (email notifications)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud reporting (advanced / export)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Priority email support</span>
                </li>
              </ul>
              
              <button
                onClick={() => handlePlanClick('business')}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap text-center block cursor-pointer mt-auto"
              >
                Select
              </button>
            </div>

            {/* ProAdvisor Plan */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border-2 border-indigo-200 hover:border-indigo-300 flex flex-col h-full relative">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">ProAdvisor</h3>
                <p className="text-sm text-gray-500 mb-2">(Bookkeepers & Firms)</p>
                <div className="text-4xl font-bold text-gray-900 mb-1">
                  {formatPrice(basePrices.proAdvisor)}<span className="text-lg text-gray-600">{getPeriodText()}</span>
                </div>
              </div>
              
              <ul className="space-y-2.5 mb-8 flex-grow">
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Everything in Business</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">QBOVerifier bioStamp (anti-tamper)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Styled documents</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Documents/month (optionally pooled)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Custom branding (banner + logo + colors + footer)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud checks (tamper + integrity validation)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud alerts (email notifications)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Fraud reporting (advanced / export)</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Priority email support</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Multi-realm (up to 10 client realms included "QuickBooks companies")</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Central billing</span>
                </li>
              </ul>
              
              <button
                onClick={() => handlePlanClick('proAdvisor')}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap text-center block cursor-pointer mt-auto"
              >
                Select
              </button>

										 
																						 
																																																																																																																																	
							 
																					
											 
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border-2 border-gray-100 hover:border-blue-200 flex flex-col h-full relative">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Enterprise</h3>
                <p className="text-sm text-transparent mb-2">&nbsp;</p>
                <div className="text-4xl font-bold text-gray-900 mb-1">Custom</div>
              </div>
              
              <ul className="space-y-2.5 mb-8 flex-grow">
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Everything in Business</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Unlimited documents</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
									<span className="text-gray-700 text-sm">Unlimited users</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Custom integrations</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Dedicated account manager</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">Priority email and phone support</span>
                </li>
              </ul>
              
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/contact')}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap text-center block cursor-pointer mt-auto"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            {/* Hidden test payment icon — click to go to $1 test payment */}
            <div className="flex justify-center mb-4">
              <button
                onClick={() => navigate('/payment', { state: { planId: '9', term: 'M' } })}
                className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center cursor-pointer hover:bg-purple-150 transition-colors duration-200"
                title=""
                aria-label=""
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-violet-600 flex items-center justify-center">
                  <i className="ri-secure-payment-line text-white text-sm w-4 h-4 flex items-center justify-center"></i>
                </div>
              </button>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our pricing and features.
            </p>
          </div>
          
          <div className="space-y-8">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Is there a free trial available?
              </h3>
              <p className="text-gray-600">
                Yes! You can select the Free plan above to experience the full value of the solution. The first 5 invoices are styled for your company invoices. Subsequent invoices will default to the standard vanilla style.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                What happens if I exceed my invoice limit?
              </h3>
              <p className="text-gray-600">
                We'll notify you when you're approaching your limit. You can upgrade your plan anytime or purchase additional invoice credits.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Can I cancel my subscription anytime?
              </h3>
              <p className="text-gray-600">
                Absolutely. You can cancel your subscription at any time. Your account will remain active until the end of your billing period.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Do you offer discounts for annual billing?
              </h3>
              <p className="text-gray-600">
                Yes! Save 15% when you choose annual billing. Contact our sales team for custom Enterprise pricing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Share Bar */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-gray-500 text-sm font-medium uppercase tracking-widest mb-5">
            Happy with QBOVerifier? Spread the word
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://qboverifier.bio-mail.com/pricing')}&title=${encodeURIComponent('Finally — invoice fraud protection built for QuickBooks Online. Check out QBOVerifier.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#0A66C2] hover:bg-[#0958A8] text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-linkedin-fill text-base w-4 h-4 flex items-center justify-center"></i>
              Share on LinkedIn
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent('https://qboverifier.bio-mail.com')}&text=${encodeURIComponent('Just found QBOVerifier — cryptographic invoice fraud prevention built for QuickBooks Online. AES-256 encryption on every invoice. Genius. #InvoiceFraud #QuickBooks #CyberSecurity')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-black hover:bg-gray-800 text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-twitter-x-line text-base w-4 h-4 flex items-center justify-center"></i>
              Share on X
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText('https://qboverifier.bio-mail.com');
                const btn = document.getElementById('copy-link-btn');
                if (btn) {
                  btn.textContent = ' Copied!';
                  setTimeout(() => { if (btn) btn.textContent = ' Copy Link'; }, 2000);
                }
              }}
              className="inline-flex items-center gap-2 border-2 border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-800 font-semibold px-6 py-3 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-link text-base w-4 h-4 flex items-center justify-center"></i>
              <span id="copy-link-btn">Copy Link</span>
            </button>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Secure Your Invoices?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Select your Free or Paid plan today and see how QBOVerifier can protect your business from invoice fraud.
          </p>
          <button
            onClick={() => handlePlanClick('free')}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
          >
            <i className="ri-rocket-line mr-2"></i>
            Start Today!
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

